/**
 * SG AGM UFS — 엔티티 Profile(/api/entity/profile) 기반 폼 자동 채움.
 */
import { dateToHtmlInput } from '../../../change-financial-year-end/composables/dateToHtmlInput';
import type { AgmUfsFormModel } from './useFormPayload';

function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

function pickFirst(...values: unknown[]): string {
  for (const v of values) {
    const s = trim(v);
    if (s) return s;
  }
  return '';
}

/** L_ENTITY 등록 주소 컴포넌트 → registered_office_address 한 줄 */
export function formatRegisteredOfficeAddressFromProfile(profileRaw: Record<string, any>): string {
  const profile = (profileRaw?.data as Record<string, any>) || profileRaw || {};
  const reg = (profile.registeredAddress as Record<string, any>) || {};

  const postalCode = pickFirst(reg.postalCode, reg.POSTAL_CODE);
  const blockNo = pickFirst(reg.blockHouseNo, reg.blockNo, reg.BLOCK_NO);
  const streetName = pickFirst(reg.streetName, reg.STREET_NAME);
  const buildingName = pickFirst(reg.buildingEstateName, reg.buildingName, reg.BUILDING_NAME);
  const addrLevel = pickFirst(reg.level, reg.addrLevel, reg.ADDR_LEVEL);
  const addrUnit = pickFirst(reg.unit, reg.addrUnit, reg.ADDR_UNIT);

  const alt = (profile.alternateAddress as Record<string, any>) || {};
  const foreign1 = pickFirst(
    reg.foreignAddressLine1,
    reg.FOREIGN_ADDRESS_LINE_1,
    alt.foreignAddressLine1,
    alt.line1,
    alt.FOREIGN_ADDRESS_LINE_1,
  );
  const foreign2 = pickFirst(
    reg.foreignAddressLine2,
    reg.FOREIGN_ADDRESS_LINE_2,
    alt.foreignAddressLine2,
    alt.line2,
    alt.FOREIGN_ADDRESS_LINE_2,
  );

  const hasLocal = Boolean(postalCode || blockNo || streetName || buildingName || addrLevel || addrUnit);
  if (!hasLocal && (foreign1 || foreign2)) {
    return [foreign1, foreign2].filter(Boolean).join(', ');
  }

  let address = blockNo;
  if (streetName) address += (address ? ' ' : '') + streetName;
  if (addrLevel) address += (address ? ' #' : '#') + addrLevel;
  if (addrUnit) address += (address ? '-' : '') + addrUnit;
  if (buildingName || postalCode) {
    address += ' ';
    if (buildingName) {
      address += buildingName;
      if (postalCode) address += ' ';
    }
    if (postalCode) address += `SINGAPORE ${postalCode}`;
  }
  return address.trim();
}

/**
 * FINANCIAL_YEAR_END, AGM_DUEDATE, 등록 사무소 주소를 폼에 반영 (비어 있을 때만).
 */
function unwrapEntityProfile(profileRaw: Record<string, any>): Record<string, any> {
  let p = profileRaw;
  for (let i = 0; i < 2; i += 1) {
    const inner = p?.data;
    if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
      const hasProfileShape =
        inner.accountingDates != null ||
        inner.registeredAddress != null ||
        inner.regNo != null ||
        inner.REG_NO != null;
      if (hasProfileShape) {
        p = inner as Record<string, any>;
        continue;
      }
    }
    break;
  }
  return p;
}

export function applyEntityProfileToAgmUfsForm(
  model: AgmUfsFormModel,
  profileRaw: Record<string, any> | null | undefined,
): void {
  if (!profileRaw || typeof profileRaw !== 'object') return;

  const profile = unwrapEntityProfile(profileRaw);
  const accounting = (profile.accountingDates as Record<string, any>) || {};

  const fye = dateToHtmlInput(
    accounting.financialYearEnd ??
      profile.financialYearEnd ??
      profile.FINANCIAL_YEAR_END,
  );
  const agmDue = dateToHtmlInput(
    accounting.nextAGMDueDate ?? profile.agmDuedate ?? profile.AGM_DUEDATE,
  );
  const officeAddress = formatRegisteredOfficeAddressFromProfile(profile);

  if (fye && !trim(model.financialYearEndDate)) {
    model.financialYearEndDate = fye;
  }
  if (agmDue && !trim(model.agmDate)) {
    model.agmDate = agmDue;
  }
  if (officeAddress && !trim(model.registeredOfficeAddress)) {
    model.registeredOfficeAddress = officeAddress;
  }
}
